import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Share2, Mail, Loader2, QrCode, Send, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const copyText = async (text, msg = "Copied") => {
  try { await navigator.clipboard.writeText(text); toast.success(msg); } catch { toast.error("Couldn't copy — select and copy it manually"); }
};

export const shareText = async ({ title, text, url }) => {
  try { if (navigator.share) { await navigator.share({ title, text, url }); return; } }
  catch (e) { if (e?.name === "AbortError") return; }
  copyText(url || text, url ? "Link copied" : "Copied");
};

// The password travels separately from the link: copy it, use the phone's share
// sheet (WhatsApp/SMS), or open the user's own email app — it never goes through our server.
export function PasswordShareOptions({ password, testId = "pw-share" }) {
  const [show, setShow] = useState(false);
  const text = `Password: ${password}`;
  const mailto = `mailto:?subject=${encodeURIComponent("The password you'll need")}&body=${encodeURIComponent(`${text}\n\nUse it to open the link I sent you separately.`)}`;
  return (
    <div className="space-y-2 rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/30 p-3" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs">
        <KeyRound className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300" />
        <span className="font-medium text-amber-800 dark:text-amber-200">Send the password separately</span>
        <button type="button" onClick={() => setShow((v) => !v)} className="ml-auto font-mono text-amber-900 dark:text-amber-100" data-testid={`${testId}-value`}>{show ? password : "••••••••"}</button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => copyText(password, "Password copied")} data-testid={`${testId}-copy`}><Copy className="h-3.5 w-3.5 mr-1.5" />Copy</Button>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => shareText({ title: "Password", text })} data-testid={`${testId}-share`}><Share2 className="h-3.5 w-3.5 mr-1.5" />Share…</Button>
        <Button size="sm" variant="outline" className="rounded-full" asChild><a href={mailto} data-testid={`${testId}-email`}><Mail className="h-3.5 w-3.5 mr-1.5" />Email</a></Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Tip: send it a different way than the link — e.g. link by email, password by text.</p>
    </div>
  );
}

// One place for "how do I send this link?": email it, copy it, share sheet, or QR.
export default function LinkShareOptions({ url, title, onEmail, testId = "link-share" }) {
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const send = async () => {
    const list = emails.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
    if (!list.length) { toast.error("Add at least one email address"); return; }
    if (list.some((e) => !EMAIL_RE.test(e))) { toast.error("One of those email addresses doesn't look right"); return; }
    if (list.length > 10) { toast.error("Up to 10 people at a time"); return; }
    setBusy(true);
    try {
      const r = await onEmail(list);
      if (r.sent === r.requested) { toast.success(`Emailed to ${r.sent} ${r.sent === 1 ? "person" : "people"}`); setEmails(""); }
      else toast.error(`Sent ${r.sent} of ${r.requested} — check the addresses`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't send the email");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="text-xs font-mono break-all rounded-xl border border-border bg-background/60 p-2.5" data-testid={`${testId}-url`}>{url}</div>
      {onEmail && (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400" />
            <Input type="email" multiple value={emails} onChange={(e) => setEmails(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder="friend@email.com, another@email.com" className="pl-9" data-testid={`${testId}-email-input`} />
          </div>
          <Button onClick={send} disabled={busy || !emails.trim()} className="rounded-full shrink-0" data-testid={`${testId}-email-send`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}<span className="ml-1.5">Email</span>
          </Button>
        </div>
      )}
      <div className="grid grid-cols-3 gap-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => copyText(url, "Link copied")} data-testid={`${testId}-copy`}><Copy className="h-3.5 w-3.5 mr-1.5" />Copy</Button>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => shareText({ title, url })} data-testid={`${testId}-share`}><Share2 className="h-3.5 w-3.5 mr-1.5" />Share…</Button>
        <Button size="sm" variant={showQr ? "default" : "outline"} className="rounded-full" onClick={() => setShowQr((v) => !v)} data-testid={`${testId}-qr-toggle`}><QrCode className="h-3.5 w-3.5 mr-1.5" />QR</Button>
      </div>
      {showQr && (
        <div className="flex items-center gap-3" data-testid={`${testId}-qr`}>
          <div className="bg-white p-2 rounded-xl shrink-0"><QRCodeSVG value={url} size={128} level="M" /></div>
          <p className="text-xs text-muted-foreground">Let them scan this with their phone camera, or screenshot it to send.</p>
        </div>
      )}
    </div>
  );
}
